import { ref, computed } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useRouter } from 'vue-router'
import api from '../services/api'

const ADMIN_SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const SESSION_WARNING_THRESHOLD = 5 * 60 * 1000 // Warn in the last 5 minutes
const ACTIVITY_CHECK_INTERVAL = 30 * 1000 // UI + expiry check interval
const ACTIVITY_UPDATE_THROTTLE = 5 * 1000 // Min gap between activity writes

const pluralRu = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

// Shared singleton state — one session across all components
const adminSessionActive = ref(false)
const lastActivity = ref(0)
let sessionIntervalId: ReturnType<typeof setInterval> | null = null
let initialized = false

const readStoredActivity = (): number | null => {
  const raw = localStorage.getItem('admin_last_activity')
  if (raw == null || raw === '') return null
  const parsed = parseInt(raw, 10)
  return Number.isNaN(parsed) ? null : parsed
}

const hasAdminSessionKeys = (): boolean => {
  const storedUser = localStorage.getItem('admin_session_user')
  return (
    localStorage.getItem('admin_session_active') === 'true' &&
    readStoredActivity() != null &&
    storedUser != null &&
    storedUser !== ''
  )
}

const isSessionExpired = (): boolean => {
  const activity = readStoredActivity()
  if (activity == null) return true
  return Date.now() - activity >= ADMIN_SESSION_TIMEOUT
}

const isStoredSessionValid = (): boolean => hasAdminSessionKeys() && !isSessionExpired()

const getRemainingMs = (): number => {
  const activity = readStoredActivity()
  if (activity == null) return 0
  return Math.max(0, ADMIN_SESSION_TIMEOUT - (Date.now() - activity))
}

/** Human-readable remaining time for the session warning banner. */
export const formatSessionRemaining = (remainingMs: number): string => {
  if (remainingMs <= 0) return 'сейчас'

  const totalSeconds = Math.ceil(remainingMs / 1000)
  if (totalSeconds < 60) {
    return `${totalSeconds} ${pluralRu(totalSeconds, 'секунду', 'секунды', 'секунд')}`
  }

  const minutes = Math.ceil(remainingMs / 60000)
  return `${minutes} ${pluralRu(minutes, 'минуту', 'минуты', 'минут')}`
}

const syncFromStorage = (authUserId?: number | string | null): boolean => {
  if (!hasAdminSessionKeys()) {
    adminSessionActive.value = false
    lastActivity.value = 0
    return false
  }

  if (isSessionExpired()) {
    adminSessionActive.value = false
    lastActivity.value = 0
    return false
  }

  const storedUser = localStorage.getItem('admin_session_user')
  if (authUserId != null && storedUser !== String(authUserId)) {
    adminSessionActive.value = false
    lastActivity.value = 0
    return false
  }

  adminSessionActive.value = true
  lastActivity.value = readStoredActivity() ?? 0
  return true
}

const clearStoredSession = () => {
  localStorage.removeItem('admin_session_active')
  localStorage.removeItem('admin_last_activity')
  localStorage.removeItem('admin_session_user')
}

const stopSessionMonitoring = () => {
  if (sessionIntervalId) {
    clearInterval(sessionIntervalId)
    sessionIntervalId = null
  }
}

export const useAdminAccess = () => {
  const authStore = useAuthStore()
  const router = useRouter()

  const isSuperuser = computed(() => authStore.user?.is_superuser === true)

  const isAdminSessionValid = computed(() => {
    if (!isStoredSessionValid()) return false

    if (!authStore.isInitialized || authStore.user?.id == null) {
      return true
    }

    const storedUser = localStorage.getItem('admin_session_user')
    return storedUser === String(authStore.user.id)
  })

  const sessionRemainingMs = computed(() => {
    if (!hasAdminSessionKeys()) return 0
    return getRemainingMs()
  })

  const sessionWarningText = computed(() => formatSessionRemaining(sessionRemainingMs.value))

  const shouldShowWarning = computed(() => {
    if (!isAdminSessionValid.value) return false
    const remaining = getRemainingMs()
    return remaining > 0 && remaining <= SESSION_WARNING_THRESHOLD
  })

  const touchActivity = (force = false) => {
    const sessionFlag = localStorage.getItem('admin_session_active') === 'true'
    if (!sessionFlag && !force) return

    const now = Date.now()
    const stored = readStoredActivity()
    if (!force && stored != null && now - stored < ACTIVITY_UPDATE_THROTTLE) {
      return
    }

    lastActivity.value = now
    localStorage.setItem('admin_last_activity', now.toString())
    localStorage.setItem('admin_session_active', 'true')

    const uid = authStore.user?.id
    if (uid != null) {
      localStorage.setItem('admin_session_user', String(uid))
    }

    adminSessionActive.value = true
  }

  const stampAdminActivity = () => touchActivity(true)

  const exitAdminMode = (reason = 'manual_exit') => {
    if (!router.currentRoute.value.path.startsWith('/admin')) {
      stopSessionMonitoring()
      adminSessionActive.value = false
      lastActivity.value = 0
      clearStoredSession()
      return
    }

    logAdminAction('admin_session_ended', {
      method: reason,
      route: router.currentRoute.value.fullPath,
    })

    adminSessionActive.value = false
    lastActivity.value = 0
    clearStoredSession()
    stopSessionMonitoring()

    if (authStore.organizationId) {
      router.push({ name: 'dashboard', params: { orgId: authStore.organizationId } })
    } else {
      router.push({ name: 'login' })
    }
  }

  const enforceSessionOrExit = (): boolean => {
    if (!router.currentRoute.value.path.startsWith('/admin')) {
      return true
    }

    const userId = authStore.isInitialized ? authStore.user?.id : null
    syncFromStorage(userId)

    if (authStore.isInitialized && userId != null) {
      const storedUser = localStorage.getItem('admin_session_user')
      if (storedUser && storedUser !== String(userId)) {
        exitAdminMode('user_mismatch')
        return false
      }
    }

    if (!hasAdminSessionKeys()) {
      if (authStore.isInitialized && userId != null && authStore.user?.is_superuser) {
        stampAdminActivity()
        return true
      }
      return true
    }

    if (isSessionExpired()) {
      exitAdminMode('timeout')
      return false
    }

    return true
  }

  const storeAuditLocally = (logEntry: Record<string, unknown>) => {
    try {
      const auditLogs = JSON.parse(localStorage.getItem('admin_audit_logs') || '[]')
      auditLogs.push(logEntry)
      if (auditLogs.length > 100) {
        auditLogs.splice(0, auditLogs.length - 100)
      }
      localStorage.setItem('admin_audit_logs', JSON.stringify(auditLogs))
    } catch (error) {
      console.error('Failed to store audit log locally:', error)
    }
  }

  const logAdminAction = (action: string, details?: Record<string, unknown>) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      user_id: authStore.user?.id,
      user_email: authStore.user?.email,
      action,
      details: {
        ...details,
        user_agent: navigator.userAgent,
        url: window.location.href,
      },
    }

    api.post('/admin/audit/log', logEntry).catch((error) => {
      console.error('Failed to log admin action:', error)
      storeAuditLocally(logEntry)
    })
  }

  const startSessionMonitoring = () => {
    stopSessionMonitoring()
    sessionIntervalId = setInterval(() => {
      enforceSessionOrExit()
    }, ACTIVITY_CHECK_INTERVAL)
  }

  const requestAdminAccess = async (): Promise<void> => {
    if (!isSuperuser.value) {
      const error = 'Недостаточно прав доступа: требуется статус суперпользователя'
      logAdminAction('admin_access_denied', {
        reason: 'insufficient_permissions',
        user_role: authStore.user?.is_owner ? 'organization_owner' : 'regular_user',
      })
      throw new Error(error)
    }

    if (!authStore.user?.id) {
      throw new Error('Профиль пользователя не загружен. Повторите попытку.')
    }

    const validationResponse = await api.post('/admin/access/validate', {
      user_id: authStore.user.id,
      requested_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      ip_address: window.location.hostname,
    })

    if (!validationResponse.data.access_granted) {
      throw new Error(validationResponse.data.reason || 'Доступ запрещен сервером')
    }

    logAdminAction('admin_access_granted', {
      method: 'profile_dropdown',
      server_validation: true,
    })

    stampAdminActivity()
    localStorage.setItem('admin_session_user', String(authStore.user.id))
    startSessionMonitoring()
  }

  const checkAdminSession = (): boolean => syncFromStorage(authStore.user?.id)

  const updateActivity = () => {
    if (localStorage.getItem('admin_session_active') === 'true') {
      touchActivity()
    }
  }

  const extendSession = () => {
    if (!hasAdminSessionKeys() && !authStore.user?.id) return
    stampAdminActivity()
    logAdminAction('admin_session_extended', { method: 'user_action' })
  }

  const refreshSessionState = () => {
    const userId = authStore.isInitialized ? authStore.user?.id : null
    return syncFromStorage(userId)
  }

  const forceLogout = () => {
    logAdminAction('admin_force_logout', { reason: 'security_policy' })
    exitAdminMode('force_logout')
  }

  if (typeof window !== 'undefined' && !initialized) {
    initialized = true
    if (isStoredSessionValid()) {
      syncFromStorage()
      startSessionMonitoring()
    }
  }

  return {
    adminSessionActive,
    lastActivity,
    isAdminSessionValid,
    sessionRemainingMs,
    sessionWarningText,
    shouldShowWarning,
    requestAdminAccess,
    exitAdminMode,
    checkAdminSession,
    updateActivity,
    extendSession,
    stampAdminActivity,
    logAdminAction,
    forceLogout,
    startSessionMonitoring,
    stopSessionMonitoring,
    refreshSessionState,
    enforceSessionOrExit,
    ADMIN_SESSION_TIMEOUT,
    SESSION_WARNING_THRESHOLD,
    ACTIVITY_CHECK_INTERVAL,
  }
}
